import React from 'react';
import { useActiveUnderlying } from '../../utils/activeUnderlying';
import { useIvIndicator } from '../../services/ivTracking';
import { isDesignSystemOn } from '../../theme/mmecDsPreview';
import './NetGexStrip.ds.css';

/**
 * Compact net-GEX header for the Vol/OI/SG view. Reads the same backend indicator
 * as the IV/Vol panel and the TradingView feed, so the net GEX / gamma flip /
 * walls / regime shown here match those everywhere. Tracks the active underlying.
 */
const BULL = '#3fb37f';
const BEAR = '#e0533d';
const MUT2 = '#68728a';

const num = (v: number | null | undefined, d = 0) =>
  v == null || !Number.isFinite(v) ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: d });

const Cell: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
  <div className="ngx-cell">
    <span className="ngx-l">{label}</span>
    <span className="ngx-v" style={color ? { color } : undefined}>{value}</span>
  </div>
);

const NetGexStrip: React.FC = () => {
  const symbol = useActiveUnderlying('SPY');
  const { data } = useIvIndicator(symbol);
  const g = data && data.ok ? data.gex : null;
  const neg = g?.regime === 'negative';
  // Design System preview: regime negative = gold, positive = up; walls are levels (magenta), not direction.
  const ds = isDesignSystemOn();
  const regimeColor = ds ? (neg ? 'var(--regime-negative)' : 'var(--regime-positive)') : neg ? BEAR : BULL;
  const callWallColor = ds ? 'var(--level-cw-pw)' : BULL;
  const putWallColor = ds ? 'var(--level-cw-pw)' : BEAR;

  return (
    <div className="ngx-root">
      <style>{CSS}</style>
      <div className="ngx-title">
        <span className="ngx-sym">{symbol}</span>
        <span className="ngx-cap">Net gamma exposure</span>
      </div>
      {g && g.ok ? (
        <div className="ngx-cells">
          <Cell label="Net GEX" value={g.net_gex_bn == null ? '—' : `$${g.net_gex_bn.toFixed(2)}bn`} color={regimeColor} />
          <Cell label="Regime" value={<span className="ngx-pill" style={{ color: regimeColor, borderColor: regimeColor }}>{neg ? 'negative γ' : 'positive γ'}</span>} />
          <Cell label="Gamma flip" value={num(g.gamma_flip, 0)} />
          <Cell label="Call wall" value={num(g.call_wall, 0)} color={callWallColor} />
          <Cell label="Put wall" value={num(g.put_wall, 0)} color={putWallColor} />
          <Cell label="Spot" value={num(data?.underlying ?? g.spot, 2)} />
        </div>
      ) : (
        <div className="ngx-empty">
          {ds && <span className="ngx-ds-sk" aria-hidden="true" />}
          Net GEX loads for the tracked underlying…
        </div>
      )}
    </div>
  );
};

const CSS = `
.ngx-root{background:#12151c;border:1px solid #232a36;border-radius:12px;padding:11px 14px;margin:0 0 10px;
  font:13px/1.4 -apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif;color:#e6ebf2;
  display:flex;align-items:center;gap:18px;flex-wrap:wrap}
.ngx-title{display:flex;flex-direction:column;gap:2px;min-width:90px}
.ngx-sym{font-family:'IBM Plex Mono',ui-monospace,monospace;font-weight:600;color:#4c9aff;font-size:14px}
.ngx-cap{font-size:10px;text-transform:uppercase;letter-spacing:.6px;color:${MUT2}}
.ngx-cells{display:flex;gap:20px;flex-wrap:wrap;align-items:center}
.ngx-cell{display:flex;flex-direction:column;gap:2px}
.ngx-l{font-size:10px;text-transform:uppercase;letter-spacing:.5px;color:${MUT2}}
.ngx-v{font-size:16px;font-weight:640;font-family:'IBM Plex Mono',ui-monospace,monospace;font-variant-numeric:tabular-nums}
.ngx-pill{display:inline-block;font-size:11px;border:1px solid;border-radius:5px;padding:1px 7px;font-family:'IBM Plex Mono',ui-monospace,monospace}
.ngx-empty{color:${MUT2};font-size:12px}
`;

export default NetGexStrip;
